/**
 * NewImportPage — multi-step wizard for spreadsheet/vCard/LinkedIn imports.
 *
 * Steps: Upload -> Field Mapping -> Preview + Dedup -> Tag + Priority -> Commit.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, ArrowRight, CheckCircle, Loader2, Upload } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import FieldMapperTable from '../components/FieldMapperTable';
import DedupPreviewTable from '../components/DedupPreviewTable';
import importService from '../services/importService';
import {
  ImportBatch,
  PreviewRecord,
  PreviewResponse,
  SourceType,
} from '../types/importBatch';

type Step = 1 | 2 | 3 | 4 | 5;
type Action = 'create' | 'merge' | 'skip';

const PRIORITIES: Array<{ value: number; label: string }> = [
  { value: 1, label: 'High (process first)' },
  { value: 5, label: 'Normal' },
  { value: 10, label: 'Low (bulk backfill)' },
];

const NewImportPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState<ImportBatch | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [sample, setSample] = useState<Record<string, any>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [actions, setActions] = useState<Record<number, Action>>({});
  // Row indices the operator has explicitly confirmed via the picker.
  // Strong-match rows stay unconfirmed (and block Continue) until touched.
  const [confirmed, setConfirmed] = useState<Set<number>>(new Set());
  const [tagsInput, setTagsInput] = useState('');
  const [groupName, setGroupName] = useState('');
  const [priority, setPriority] = useState<number>(5);
  const [skipResearch, setSkipResearch] = useState(false);

  const tags = useMemo(
    () => tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
    [tagsInput]
  );

  // Step 1: Upload
  const handleUpload = async (file: File, sourceType?: SourceType) => {
    setBusy(true);
    const res = await importService.uploadImportFile(file, sourceType);
    setBusy(false);
    if (res.error || !res.data) {
      toast.error(res.error || 'Upload failed');
      return;
    }
    setBatch(res.data);
    // Pull preview metadata: re-fetch status to get the saved field_mapping.
    const status = await importService.getStatus(res.data.id);
    if (status.data) {
      const fm = (status.data as any).field_mapping || {};
      setMapping(fm);
      // Derive columns from mapping keys + first sample row keys.
      const cols = new Set<string>(Object.keys(fm));
      // Pull from preview if no columns yet.
      setColumns(Array.from(cols));
    }
    // Run a preview pre-fetch to collect records (which surfaces all source columns).
    const pv = await importService.getPreview(res.data.id);
    if (pv.data) {
      setPreview(pv.data);
      // Build the column list from any record's record-keys.
      const colSet = new Set<string>(Object.keys(mapping));
      const sampleRows: Record<string, any>[] = [];
      pv.data.records.forEach((r) => {
        Object.keys(r.record).forEach((k) => colSet.add(k));
        if (sampleRows.length < 1) sampleRows.push(r.record);
      });
      setColumns(Array.from(colSet));
      setSample(sampleRows);
    }
    setStep(2);
  };

  // Step 2 -> 3: save mapping, regenerate preview.
  const handleSaveMapping = async () => {
    if (!batch) return;
    setBusy(true);
    const saved = await importService.saveMapping(batch.id, mapping);
    if (saved.error) {
      setBusy(false);
      toast.error(saved.error);
      return;
    }
    const pv = await importService.getPreview(batch.id);
    setBusy(false);
    if (pv.error || !pv.data) {
      toast.error(pv.error || 'Preview failed');
      return;
    }
    setPreview(pv.data);
    const initial: Record<number, Action> = {};
    pv.data.records.forEach((r: PreviewRecord) => {
      const fallback = (r.suggested_action as string) || 'create';
      initial[r.row] = fallback === 'review' ? 'merge' : (fallback as Action);
    });
    setActions(initial);
    setStep(3);
  };

  // Step 5: Commit
  const handleCommit = async () => {
    if (!batch) return;
    setBusy(true);
    const dedup_actions: Record<string, Action> = {};
    Object.entries(actions).forEach(([k, v]) => {
      dedup_actions[k] = v;
    });
    const res = await importService.commit(batch.id, {
      tags,
      research_priority: priority,
      dedup_actions,
      skip_research: skipResearch,
      group_name: groupName.trim() || undefined,
    });
    setBusy(false);
    if (res.error || !res.data) {
      toast.error(res.error || 'Commit failed');
      return;
    }
    toast.success(
      `Imported ${res.data.imported_records} investor(s); queued ${res.data.research_queued} for research.`
    );
    navigate(`/imports/${batch.id}`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/imports')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to imports
          </button>
          <div className="text-sm text-gray-500">Step {step} of 5</div>
        </div>

        <h1 className="text-2xl font-bold text-gray-900">New investor import</h1>

        {step === 1 && <UploadStep busy={busy} onUpload={handleUpload} />}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Map source columns to investor fields
              </h2>
              <button
                onClick={async () => {
                  if (!batch) return;
                  // Re-run auto-detect by re-uploading mapping
                  const res = await importService.getStatus(batch.id);
                  const auto = (res.data as any)?.field_mapping || {};
                  setMapping(auto);
                  toast.success('Re-applied suggested mapping');
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Apply suggested mappings
              </button>
            </div>
            <FieldMapperTable
              columns={columns}
              mapping={mapping}
              onChange={setMapping}
              sample={sample}
              targetType={batch?.target_type === 'company' ? 'company' : 'investor'}
            />
            <div className="flex justify-end">
              <button
                onClick={handleSaveMapping}
                disabled={busy}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Continue to preview
              </button>
            </div>
          </div>
        )}

        {step === 3 && preview && (() => {
          const decisionsOutstanding = preview.records.filter(
            (r) =>
              (r.suggested_action === 'merge' ||
                r.suggested_action === 'review') &&
              !confirmed.has(r.row),
          ).length;
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-green-700">Create: {preview.create_count}</span>
                <span className="text-yellow-700">Merge: {preview.merge_count}</span>
                <span className="text-orange-700">Review: {preview.review_count}</span>
                <span className="text-gray-600">Total: {preview.total}</span>
              </div>
              {decisionsOutstanding > 0 && (
                <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded-lg p-3 text-sm">
                  <strong>{decisionsOutstanding}</strong> potential duplicate
                  {decisionsOutstanding === 1 ? '' : 's'} need a decision.
                  Pick <em>Merge with existing</em>, <em>Create new</em>, or
                  <em> Skip</em> on every flagged row before continuing — no
                  silent merges.
                </div>
              )}
              <DedupPreviewTable
                records={preview.records}
                actions={actions}
                confirmed={confirmed}
                onChange={(row, action) => {
                  setActions((prev) => ({ ...prev, [row]: action }));
                  setConfirmed((prev) => {
                    const next = new Set(prev);
                    next.add(row);
                    return next;
                  });
                }}
              />
              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </button>
                <button
                  onClick={() => setStep(4)}
                  disabled={decisionsOutstanding > 0}
                  title={
                    decisionsOutstanding > 0
                      ? `Resolve ${decisionsOutstanding} pending duplicate decision${
                          decisionsOutstanding === 1 ? '' : 's'
                        } first`
                      : ''
                  }
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <ArrowRight className="w-4 h-4 mr-2" /> Continue to tags & priority
                </button>
              </div>
            </div>
          );
        })()}

        {step === 4 && (
          <div className="space-y-6 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group name <span className="text-gray-400 font-normal">(creates the group)</span>
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder='e.g. "MTW", "Techstars Detroit 25"'
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Every imported row will be tagged with this group name. The
                group appears in the Group filter on the Companies and
                Investors pages and is selectable for cohort reports. If
                this name doesn't exist yet, it's created automatically; if
                it does, the new rows are added to it.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags (comma-separated, applied to all imported investors)
              </label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="e.g. funded-house, q2-conference"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {tags.map((t) => (
                    <span
                      key={t}
                      className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Research priority
              </label>
              <div className="space-y-1">
                {PRIORITIES.map((p) => (
                  <label
                    key={p.value}
                    className="flex items-center text-sm text-gray-700"
                  >
                    <input
                      type="radio"
                      name="priority"
                      value={p.value}
                      checked={priority === p.value}
                      onChange={() => setPriority(p.value)}
                      className="mr-2"
                    />
                    {p.label}
                  </label>
                ))}
              </div>
              <label className="flex items-center text-sm text-gray-700 mt-2">
                <input
                  type="checkbox"
                  checked={skipResearch}
                  onChange={(e) => setSkipResearch(e.target.checked)}
                  className="mr-2"
                />
                Skip auto-research (import only)
              </label>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(3)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <button
                onClick={() => setStep(5)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Review & commit <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {step === 5 && preview && (
          <div className="space-y-4 max-w-xl">
            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-3">
              <h2 className="text-lg font-semibold text-gray-900">Ready to import</h2>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>
                  <strong>Source:</strong> {batch?.source_file} (
                  {batch?.source_type})
                </li>
                <li>
                  <strong>Records:</strong> {preview.total} (create:{' '}
                  {Object.values(actions).filter((a) => a === 'create').length}, merge:{' '}
                  {Object.values(actions).filter((a) => a === 'merge').length}, skip:{' '}
                  {Object.values(actions).filter((a) => a === 'skip').length})
                </li>
                <li>
                  <strong>Tags:</strong>{' '}
                  {tags.length ? tags.join(', ') : '(none)'}
                </li>
                <li>
                  <strong>Research priority:</strong>{' '}
                  {PRIORITIES.find((p) => p.value === priority)?.label}
                </li>
                <li>
                  <strong>Auto-research:</strong> {skipResearch ? 'Skip' : 'Enabled'}
                </li>
              </ul>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setStep(4)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <button
                onClick={handleCommit}
                disabled={busy}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Start import
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

interface UploadStepProps {
  busy: boolean;
  onUpload: (file: File, sourceType?: SourceType) => void;
}

const UploadStep: React.FC<UploadStepProps> = ({ busy, onUpload }) => {
  const [drag, setDrag] = useState(false);

  const inferSourceType = (filename: string): SourceType | undefined => {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.csv')) {
      // Quick heuristic for LinkedIn export.
      return lower.includes('linkedin') ? 'linkedin_export' : 'csv';
    }
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return 'xlsx';
    if (lower.endsWith('.vcf') || lower.endsWith('.vcard')) return 'vcf';
    return undefined;
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    onUpload(file, inferSourceType(file.name));
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        handleFiles(e.dataTransfer.files);
      }}
      className={`bg-white border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
        drag ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
      }`}
    >
      <Upload className="w-10 h-10 mx-auto text-blue-600 mb-4" />
      <h2 className="text-lg font-semibold text-gray-900 mb-2">
        Drag & drop a file
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Accepted: CSV, XLSX, vCard (.vcf), LinkedIn Connections export
      </p>
      <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 cursor-pointer">
        {busy ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Upload className="w-4 h-4 mr-2" />
        )}
        Choose file
        <input
          type="file"
          className="hidden"
          accept=".csv,.xlsx,.xls,.vcf,.vcard"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </label>
    </div>
  );
};

export default NewImportPage;
